import React from "react"
import { Form, Formik, FormikHelpers } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import MyModal from "@common-utils/MyModal"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import { IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import { personnelSetPasswordRequest } from "./core/_requests"

interface IProps {
     personnelId: number
     personnelNom?: string
     onClose: () => void
     onDone?: () => void
}

interface IForm {
     mode?: "generate" | "manual"
     password?: string
}

// Change le mot de passe applicatif d'un collaborateur (le back envoie les identifiants par email au
// collaborateur). Deux modes : génération automatique d'un mot de passe simple, ou saisie manuelle.
// Ne touche pas au compte Google (SSO). Utilisé sur la page détails et le listing.
const ChangePasswordModal = ({ personnelId, personnelNom, onClose, onDone }: IProps) => {
     const schema = Yup.object().shape({
          mode: Yup.string().oneOf(["generate", "manual"]).required(),
          // Requis seulement en saisie manuelle
          password: Yup.string().when("mode", { is: "manual", then: s => s.min(6, "Au moins 6 caractères.").required().label(`"Mot de passe"`) }),
     })

     const initialValues: IForm = { mode: "generate", password: "" }

     function handleSubmit(values: IForm, helpers: FormikHelpers<IForm>) {
          helpers.setStatus(null)
          const payload = values.mode === "generate" ? { generate: true } : { password: values.password }
          personnelSetPasswordRequest(personnelId, payload)
               .then(() => {
                    toast.success("Mot de passe mis à jour. Les identifiants ont été envoyés au collaborateur par email.")
                    onDone?.()
                    onClose()
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })
                    helpers.setSubmitting(false)
               })
     }

     return (
          <MyModal show={true} onCloseCallback={onClose} title={<>Changer le mot de passe{personnelNom ? ` — ${personnelNom}` : ""}</>}>
               <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    {helpers => (
                         <Form noValidate autoComplete="off">
                              {helpers.status && (
                                   <MyAlert type={"danger"} classNames={"mb-4"}>
                                        {helpers.status}
                                   </MyAlert>
                              )}

                              <MyAlert type={"info"} classNames={"mb-4"} isDismissible={false}>
                                   Ceci change le mot de passe de connexion <b>à l'application</b> (identifiant/mot de passe) et envoie les nouveaux
                                   identifiants au collaborateur par email. Le compte Google (SSO) n'est pas modifié.
                              </MyAlert>

                              {/* Choix du mode */}
                              <div className="mb-4">
                                   <div className="form-check mb-2">
                                        <input
                                             className="form-check-input cursor-pointer"
                                             type="radio"
                                             id="pwd-mode-generate"
                                             checked={helpers.values.mode === "generate"}
                                             onChange={() => helpers.setFieldValue("mode", "generate")}
                                        />
                                        <label className="form-check-label cursor-pointer" htmlFor="pwd-mode-generate">
                                             Générer un mot de passe temporaire et l'envoyer
                                        </label>
                                   </div>
                                   <div className="form-check">
                                        <input
                                             className="form-check-input cursor-pointer"
                                             type="radio"
                                             id="pwd-mode-manual"
                                             checked={helpers.values.mode === "manual"}
                                             onChange={() => helpers.setFieldValue("mode", "manual")}
                                        />
                                        <label className="form-check-label cursor-pointer" htmlFor="pwd-mode-manual">
                                             Saisir un mot de passe
                                        </label>
                                   </div>
                              </div>

                              {helpers.values.mode === "manual" && (
                                   <MyInputFormField
                                        name={"password"}
                                        type={"password"}
                                        formikProps={helpers}
                                        placeholder={"Nouveau mot de passe"}
                                        classNameLayout={"mb-0"}
                                   />
                              )}

                              <div className="d-flex justify-content-end mt-5">
                                   <button
                                        type={"button"}
                                        className={"btn btn-outline btn-active-secondary btn-sm me-2"}
                                        onClick={onClose}
                                        disabled={helpers.isSubmitting}
                                   >
                                        Annuler
                                   </button>
                                   <button type={"submit"} className={"btn btn-primary btn-sm"} disabled={helpers.isSubmitting}>
                                        {!helpers.isSubmitting ? <>Enregistrer</> : <MySimpleSpinner size={"sm"} />}
                                   </button>
                              </div>
                         </Form>
                    )}
               </Formik>
          </MyModal>
     )
}

export default ChangePasswordModal
